import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Legend,
    Label,
} from "recharts";
import type { AiUsageStatsData } from "@/pages/AiConsumption/types";
import { price, quantity, percentage } from "@/utils/format";

interface AiConsumptionChartsProps {
    stats: AiUsageStatsData;
}

const COLORS = [
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#8b5cf6",
    "#ec4899",
    "#f43f5e",
    "#64748b",
    "#84cc16",
];

export const AiConsumptionCharts: React.FC<AiConsumptionChartsProps> = ({
    stats,
}) => {
    const { t } = useTranslation("global");

    const dailyData = useMemo(() => {
        return stats.daily.map((d) => ({
            ...d,
            displayDate: new Date(d.date).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
            }),
        }));
    }, [stats.daily]);

    const CustomTooltipDaily = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-[#1f1f23] border border-slate-200 dark:border-white/10 p-3 rounded-xl shadow-lg">
                    <p className="font-bold text-slate-800 dark:text-zinc-100 mb-2">
                        {label}
                    </p>
                    {payload.map((entry: any, index: number) => (
                        <div
                            key={index}
                            className="flex items-center gap-2 text-xs mb-1"
                        >
                            <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-slate-600 dark:text-zinc-400">
                                {entry.name}:
                            </span>
                            <span className="font-semibold text-slate-900 dark:text-white">
                                {entry.dataKey === "total_cost"
                                    ? price(entry.value, "euro")
                                    : quantity(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    const CustomTooltipPie = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white dark:bg-[#1f1f23] border border-slate-200 dark:border-white/10 p-3 rounded-xl shadow-lg">
                    <p className="font-bold text-slate-800 dark:text-zinc-100 mb-2">
                        {data.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs mb-1">
                        <span className="text-slate-600 dark:text-zinc-400">
                            {t("ai_consumption.total_tokens")}:
                        </span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                            {quantity(data.value)} ({percentage(data.percent)})
                        </span>
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderCustomLegend = (props: any) => {
        const { payload } = props;
        const sortedPayload = [...payload].sort(
            (a, b) => b.payload.value - a.payload.value
        );

        return (
            <ul className="w-fit mx-auto flex flex-col items-start justify-center mt-2 text-[12px] gap-1 px-4">
                {sortedPayload.map((entry, index) => (
                    <li
                        key={`item-${index}`}
                        className="flex items-center gap-2"
                    >
                        <span
                            className="w-3 h-3 rounded-sm shrink-0"
                            style={{ backgroundColor: entry.color }}
                        ></span>
                        <span className="text-slate-700 dark:text-zinc-300">
                            {entry.value}:{" "}
                            <span className="font-semibold">
                                {quantity(entry.payload.value)} (
                                {percentage(entry.payload.percent)})
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        );
    };

    const byModelData = useMemo(() => {
        const total = stats.by_model.reduce(
            (acc, curr) => acc + curr.total_tokens,
            0
        );
        return stats.by_model
            .map((m) => ({
                name: m.model,
                value: Number(m.total_tokens) || 0,
                percent:
                    total > 0
                        ? ((Number(m.total_tokens) || 0) / total) * 100
                        : 0,
            }))
            .sort((a, b) => b.value - a.value)
            .map((item, i) => ({
                ...item,
                color: COLORS[i % COLORS.length],
            }));
    }, [stats.by_model]);

    const byUserData = useMemo(() => {
        const total = stats.by_user.reduce(
            (acc, curr) => acc + curr.total_tokens,
            0
        );
        return stats.by_user
            .map((u) => ({
                name: u.name || t("ai_consumption.unknown"),
                value: Number(u.total_tokens) || 0,
                percent:
                    total > 0
                        ? ((Number(u.total_tokens) || 0) / total) * 100
                        : 0,
            }))
            .sort((a, b) => b.value - a.value)
            .map((item, i) => ({
                ...item,
                color: COLORS[i % COLORS.length],
            }));
    }, [stats.by_user, t]);

    const byContextData = useMemo(() => {
        const total = stats.by_context_type.reduce(
            (acc, curr) => acc + curr.total_tokens,
            0
        );
        return stats.by_context_type
            .map((c) => {
                const translationKey = `ai_consumption.context_types.${c.context_type}`;
                const translatedName = t(translationKey);
                return {
                    name:
                        translatedName !== translationKey
                            ? translatedName
                            : c.context_type,
                    value: Number(c.total_tokens) || 0,
                    percent:
                        total > 0
                            ? ((Number(c.total_tokens) || 0) / total) * 100
                            : 0,
                };
            })
            .sort((a, b) => b.value - a.value)
            .map((item, i) => ({
                ...item,
                color: COLORS[i % COLORS.length],
            }));
    }, [stats.by_context_type, t]);

    return (
        <div className="space-y-6">
            {/* Trend Chart */}
            <div className="bg-box rounded-2xl border border-box p-5 shadow-xs">
                <h3 className="text-center text-base font-bold text-slate-800 dark:text-zinc-100 mb-4">
                    {t("ai_consumption.trend_title")}
                </h3>
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={dailyData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient
                                    id="colorTokens"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor="#0ea5e9"
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor="#0ea5e9"
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="currentColor"
                                className="text-slate-200 dark:text-white/5"
                            />
                            <XAxis
                                dataKey="displayDate"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12 }}
                                className="text-slate-500 dark:text-zinc-400"
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => quantity(value)}
                                className="text-slate-500 dark:text-zinc-400"
                                dx={-10}
                            />
                            <RechartsTooltip
                                content={<CustomTooltipDaily />}
                                cursor={{
                                    stroke: "#94a3b8",
                                    strokeWidth: 1,
                                    strokeDasharray: "4 4",
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="total_tokens"
                                name={t("ai_consumption.total_tokens")}
                                stroke="#0ea5e9"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorTokens)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Users Chart */}
                <div className="bg-box rounded-2xl border border-box p-5 shadow-xs">
                    <h3 className="text-center text-base font-bold text-slate-800 dark:text-zinc-100 mb-4">
                        {t("ai_consumption.users_stats")}
                    </h3>
                    <div
                        style={{ height: `${200 + byUserData.length * 24}px` }}
                        className="w-full flex items-center justify-center"
                    >
                        {byUserData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={byUserData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        <Label
                                            value={quantity(
                                                byUserData.reduce(
                                                    (a, b) => a + b.value,
                                                    0
                                                )
                                            )}
                                            position="center"
                                            className="text-sm font-black fill-slate-800 dark:fill-white"
                                        />
                                        {byUserData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        content={<CustomTooltipPie />}
                                    />
                                    <Legend
                                        content={renderCustomLegend}
                                        verticalAlign="bottom"
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="text-slate-400 text-sm">
                                No data
                            </div>
                        )}
                    </div>
                </div>

                {/* Models Chart */}
                <div className="bg-box rounded-2xl border border-box p-5 shadow-xs">
                    <h3 className="text-center text-base font-bold text-slate-800 dark:text-zinc-100 mb-4">
                        {t("ai_consumption.models_stats")}
                    </h3>
                    <div
                        style={{ height: `${200 + byModelData.length * 24}px` }}
                        className="w-full flex items-center justify-center"
                    >
                        {byModelData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={byModelData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        <Label
                                            value={quantity(
                                                byModelData.reduce(
                                                    (a, b) => a + b.value,
                                                    0
                                                )
                                            )}
                                            position="center"
                                            className="text-sm font-black fill-slate-800 dark:fill-white"
                                        />
                                        {byModelData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        content={<CustomTooltipPie />}
                                    />
                                    <Legend
                                        content={renderCustomLegend}
                                        verticalAlign="bottom"
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="text-slate-400 text-sm">
                                No data
                            </div>
                        )}
                    </div>
                </div>

                {/* Context Chart */}
                <div className="bg-box rounded-2xl border border-box p-5 shadow-xs">
                    <h3 className="text-center text-base font-bold text-slate-800 dark:text-zinc-100 mb-4">
                        {t("ai_consumption.context_stats")}
                    </h3>
                    <div
                        style={{
                            height: `${200 + byContextData.length * 24}px`,
                        }}
                        className="w-full flex items-center justify-center"
                    >
                        {byContextData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={byContextData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={3}
                                        dataKey="value"
                                        stroke="none"
                                    >
                                        <Label
                                            value={quantity(
                                                byContextData.reduce(
                                                    (a, b) => a + b.value,
                                                    0
                                                )
                                            )}
                                            position="center"
                                            className="text-sm font-black fill-slate-800 dark:fill-white"
                                        />
                                        {byContextData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={entry.color}
                                            />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip
                                        content={<CustomTooltipPie />}
                                    />
                                    <Legend
                                        content={renderCustomLegend}
                                        verticalAlign="bottom"
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="text-slate-400 text-sm">
                                No data
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
